import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import type { LineItem } from '@amigodoclt/core';
import { Icon } from './Icon.js';
import { humanize, isRate, minus, money, plus } from '../lib/format.js';

type Detailed = {
  gross?: number; net?: number;
  earnings?: LineItem[]; deductions?: LineItem[]; employerContributions?: LineItem[];
  memory?: string[]; warnings?: string[];
};

type Props = {
  data: unknown;
  subtitle?: string;
  className?: string;
  /** Vidro simples em vez do destaque com listra — o protótipo reserva o destaque para um card por tela. */
  plain?: boolean;
  /** Selo A/B do comparador, no lugar do eyebrow "Resultado". */
  badge?: string;
  /** Lista plana, sem hero, grupos, memória nem impressão — o formato do comparador. */
  compact?: boolean;
  /** "Salário bruto" só vale para o salário; férias e 13º têm base própria. */
  grossLabel?: string;
  /** Quebra do líquido em parcelas; só o salário quinzenal usa. */
  installments?: { label: string; detail?: string; amount: number }[];
};

const isDetailed = (value: Detailed) =>
  typeof value.gross === 'number' && typeof value.net === 'number' && Array.isArray(value.earnings) && Array.isArray(value.deductions);

const shell = {
  layout: true as const,
  initial: { opacity: 0, x: 16 },
  animate: { opacity: 1, x: 0 },
  transition: { type: 'spring' as const, stiffness: 260, damping: 26 },
};

function Details({ icon, title, children }: { icon: string; title: string; children: ReactNode }) {
  return (
    <details className="result-details">
      <summary><Icon name={icon} size={18} />{title}<Icon name="expand_more" size={18} className="caret" /></summary>
      <div className="body">{children}</div>
    </details>
  );
}

function Row({ line, negative }: { line: LineItem; negative?: boolean }) {
  return (
    <div className={`result-row${negative ? ' negative' : ''}`}>
      <span>{line.label}{line.detail ? <small>{line.detail}</small> : null}</span>
      <strong>{negative ? minus(line.amount) : plus(line.amount)}</strong>
    </div>
  );
}

function Head({ badge, subtitle, gross }: { badge?: string; subtitle?: string; gross?: number }) {
  if (badge) {
    return (
      <div className="result-eyebrow">
        <span className="result-badge"><span className="compare-badge">{badge}</span>{subtitle}</span>
        {typeof gross === 'number' ? <span className="money">{money(gross)}</span> : null}
      </div>
    );
  }
  return (
    <div className="result-eyebrow">
      <span className="eyebrow plain"><Icon name="receipt" size={16} />Resultado</span>
      {subtitle ? <span>{subtitle}</span> : null}
    </div>
  );
}

export function ResultCard({ data, subtitle, className, plain, badge, compact, grossLabel = 'Salário bruto', installments }: Props) {
  const value = data as Detailed;
  const classes = `result-card ${plain ? 'glass' : 'glass-hero'}${compact ? ' compact' : ''}${className ? ` ${className}` : ''}`;

  if (isDetailed(value)) {
    const earnings = value.earnings ?? [];
    const deductions = value.deductions ?? [];

    if (compact) {
      return (
        <motion.aside {...shell} className={classes}>
          <Head badge={badge} subtitle={subtitle} gross={value.gross} />
          <div>
            {earnings.map((line) => <Row key={`earn-${line.label}`} line={line} />)}
            {deductions.map((line) => <Row key={`deduct-${line.label}`} line={line} negative />)}
            <div className="result-total"><span>Líquido estimado</span><strong>{money(value.net ?? 0)}</strong></div>
          </div>
        </motion.aside>
      );
    }

    return (
      <motion.aside {...shell} className={classes}>
        <Head badge={badge} subtitle={subtitle} gross={value.gross} />
        <div className="result-hero">
          <div className="hero-cell">
            <small>{grossLabel}</small>
            <strong>{money(value.gross ?? 0)}</strong>
          </div>
          <div className="hero-cell net">
            <small>Líquido estimado</small>
            <strong>{money(value.net ?? 0)}</strong>
          </div>
        </div>
        <div>
          <h2 className="result-group-title earnings"><Icon name="add_circle" size={18} />Proventos</h2>
          {earnings.map((line) => <Row key={`earn-${line.label}`} line={line} />)}
        </div>
        <div>
          <h2 className="result-group-title deductions"><Icon name="do_not_disturb_on" size={18} />Descontos</h2>
          {deductions.length
            ? deductions.map((line) => <Row key={`deduct-${line.label}`} line={line} negative />)
            : <p className="helper"><Icon name="info" size={16} />Nenhum desconto informado.</p>}
          <div className="result-total"><span>Líquido estimado</span><strong>{money(value.net ?? 0)}</strong></div>
        </div>
        {installments?.length ? (
          <div>
            <h2 className="result-group-title"><Icon name="event_repeat" size={18} />Como o líquido é pago</h2>
            <div className="installments">
              {installments.map((item) => (
                <div className="installment" key={item.label}>
                  <small>{item.label}</small>
                  <strong>{money(item.amount)}</strong>
                  {item.detail ? <span>{item.detail}</span> : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {value.employerContributions?.length ? (
          <Details icon="savings" title="FGTS e encargos que não saem do líquido">
            {value.employerContributions.map((line) => (
              <div className="result-row plain" key={line.label}><span>{line.label}</span><strong>{money(line.amount)}</strong></div>
            ))}
          </Details>
        ) : null}
        <Details icon="history_edu" title="Memória e premissas">
          <ul>
            {value.memory?.map((item) => <li key={item}>{item}</li>)}
            {value.warnings?.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </Details>
        <button type="button" className="btn-contrast print-button" onClick={() => window.print()}>
          <Icon name="print" size={20} />Imprimir resultado
        </button>
      </motion.aside>
    );
  }

  const entries = Object.entries(data as Record<string, unknown>).filter(([, item]) => typeof item === 'number') as [string, number][];
  return (
    <motion.aside {...shell} className={classes}>
      <Head badge={badge} subtitle={subtitle} />
      <div>
        {entries.map(([key, item]) => (
          <div className="result-row" key={key}>
            <span>{humanize(key)}</span>
            <strong>{isRate(key) ? String(item) : money(item)}</strong>
          </div>
        ))}
      </div>
      <Details icon="history_edu" title="Memória e premissas">
        <pre>{JSON.stringify(data, null, 2)}</pre>
      </Details>
      <button type="button" className="btn-contrast print-button" onClick={() => window.print()}>
        <Icon name="print" size={20} />Imprimir resultado
      </button>
    </motion.aside>
  );
}
